import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  formatTesSalaryCellValue,
  getTesSalaryTableCell,
  sortTesSalaryTableColumns,
  sortTesSalaryTableRows,
} from "./helpers";
import { TesSalaryTableDocument } from "./tesSalaryTablesApi";

type Props = {
  table: TesSalaryTableDocument | null;
  /** Shown next to the table name (e.g. localized TES name). */
  tesLabel?: string;
  /** Optional heading above the metadata row (e.g. "Source table"). */
  sectionLabel?: string;
};

/**
 * Read-only salary table matrix, aligned visually with {@link TesSalaryTablePickerDialog} (no cell selection).
 */
const TesSalaryTableReadOnlyGrid = ({ table, tesLabel, sectionLabel }: Props) => {
  const { t } = useTranslation("settings");

  const rows = useMemo(
    () => sortTesSalaryTableRows(table?.rows || []),
    [table],
  );
  const columns = useMemo(
    () => sortTesSalaryTableColumns(table?.columns || []),
    [table],
  );

  if (!table) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {sectionLabel && (
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          {sectionLabel}
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" gap={2} marginBottom={2}>
        <Typography fontWeight={700}>{table.name}</Typography>
        <Typography color="text.secondary">
          {t("tesSalaryTablesSelectedTes")}: {tesLabel || table.tesId}
        </Typography>
        <Typography color="text.secondary">
          {t("tesSalaryTablesEffectiveDate")}: {table.effectiveDate}
        </Typography>
      </Box>
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("tesSalaryTablesRow")}</TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.columnId}
                  align="center"
                  sx={{ minWidth: 140 }}
                >
                  <Box>
                    <Typography fontWeight={700}>{column.label}</Typography>
                    {column.secondaryLabel && (
                      <Typography variant="caption" color="text.secondary">
                        {column.secondaryLabel}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.rowId}>
                <TableCell>
                  <Box>
                    <Typography fontWeight={700}>{row.label}</Typography>
                    {row.secondaryLabel && (
                      <Typography variant="caption" color="text.secondary">
                        {row.secondaryLabel}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                {columns.map((column) => {
                  const cell = getTesSalaryTableCell(
                    table,
                    row.rowId,
                    column.columnId,
                  );
                  return (
                    <TableCell key={`${row.rowId}_${column.columnId}`} align="center">
                      {formatTesSalaryCellValue(cell)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TesSalaryTableReadOnlyGrid;
